import { type FormEvent, useState } from 'react';
import { formatDate } from '@/lib/format';
import { msg, t } from '@/lib/i18n';
import { Button, Dialog, Field, Input, Select, Stack, toast } from '@/ui';
import s from '../Admin.module.css';
import { useCreateApiKey, useRoles } from '../api';
import { ActionError } from '../components/ActionError';
import { endOfDayIso, tomorrow } from '../components/forms';
import { scopeError, ScopeFields } from '../components/ScopeFields';
import { SecretReveal } from '../components/SecretReveal';
import type { GrantScope } from '../types';

const PERIODS = [
  { value: '30', label: msg('30 days') },
  { value: '90', label: msg('90 days') },
  { value: '182', label: msg('6 months') },
  { value: 'default', label: msg('The company default (normally a year)') },
  { value: '730', label: msg('2 years') },
  { value: 'date', label: msg('Until a date') },
];

/**
 * A new API key: a role on a scope, bounded by the creator's own rights, with an expiry. The
 * secret is shown once in this dialog; afterwards only its prefix is known.
 */
export function CreateKeyDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const create = useCreateApiKey();
  const roles = useRoles(open);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [scope, setScope] = useState<GrantScope>('company');
  const [ids, setIds] = useState<string[]>([]);
  const [period, setPeriod] = useState('default');
  const [until, setUntil] = useState('');
  const [tried, setTried] = useState(false);
  const [secret, setSecret] = useState<{ key: string; expires?: string } | null>(null);
  const scopeErr = scopeError(scope, ids);

  const close = (o: boolean) => {
    if (!o) {
      setName('');
      setRole('');
      setScope('company');
      setIds([]);
      setPeriod('default');
      setUntil('');
      setTried(false);
      setSecret(null);
      create.reset();
    }
    onOpenChange(o);
  };

  const expiry = (): string | null | undefined => {
    if (period === 'default') return null; // the company's default validity
    if (period === 'date') return endOfDayIso(until) ?? undefined;
    return new Date(Date.now() + Number(period) * 86_400_000).toISOString();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    const expires_at = expiry();
    if (!name.trim() || !role || scopeErr || expires_at === undefined) return;
    create.mutate(
      { name: name.trim(), role_id: role, scope: scope as 'company' | 'projects' | 'groups', scope_ids: ids, expires_at },
      {
        onSuccess: (r) => {
          const key = typeof r.data.key === 'string' ? r.data.key : null;
          if (key) setSecret({ key, expires: typeof r.data.expires_at === 'string' ? r.data.expires_at : undefined });
          else {
            toast({ title: r.message, tone: 'success' });
            close(false);
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (secret && !o ? close(false) : close(o))}
      size="lg"
      eyebrow={t('API keys')}
      title={secret ? t('Key {name} created', { name }) : t('New API key')}
      description={
        secret
          ? secret.expires
            ? t('Valid until {date}.', { date: formatDate(secret.expires) })
            : undefined
          : t('A key acts with one role on one scope, and never with more than you hold yourself.')
      }
      footer={
        secret ? (
          <Button variant="primary" onClick={() => close(false)}>
            {t('I have copied it')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              {t('Cancel')}
            </Button>
            <Button variant="primary" type="submit" form="new-key" loading={create.isPending}>
              {t('Create key')}
            </Button>
          </>
        )
      }
    >
      {secret ? (
        <SecretReveal secret={secret.key} name={name} />
      ) : (
        <form id="new-key" onSubmit={submit} noValidate>
          <Stack gap={4}>
            <Field
              label={t('Name')}
              required
              error={tried && !name.trim() ? t('Required') : undefined}
              hint={t('What uses it, such as “Payroll export” or “Depot TV”')}
            >
              {(f) => <Input {...f} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus />}
            </Field>
            <Field label={t('Role')} required error={tried && !role ? t('Choose a role') : undefined} hint={t('Give the smallest role that does the job.')}>
              {(f) => (
                <Select
                  {...f}
                  value={role || undefined}
                  onChange={setRole}
                  placeholder={t('Choose a role')}
                  options={(roles.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
                  className={s.full}
                />
              )}
            </Field>
            <ScopeFields
              scope={scope}
              ids={ids}
              scopes={['company', 'projects', 'groups']}
              onChange={(sc, x) => {
                setScope(sc);
                setIds(x);
              }}
              error={tried ? scopeErr : undefined}
            />
            <div className={s.fieldRow}>
              <Field label={t('Valid for')}>
                {(f) => (
                  <Select {...f} value={period} onChange={setPeriod} options={PERIODS.map((p) => ({ value: p.value, label: t(p.label) }))} className={s.full} />
                )}
              </Field>
              {period === 'date' && (
                <Field label={t('Until')} required error={tried && !endOfDayIso(until) ? t('Choose a date') : undefined}>
                  {(f) => <Input {...f} type="date" min={tomorrow()} value={until} onChange={(e) => setUntil(e.target.value)} />}
                </Field>
              )}
            </div>
            <ActionError error={create.error} />
          </Stack>
        </form>
      )}
    </Dialog>
  );
}
